import { millisecondsToSeconds } from 'date-fns';

import type { RigsChart } from 'src/api/chart/rigs-chart-api';
import { hasValue } from 'src/shared/utils/common';
import type { TPadReadiness, TPreparationStatus } from 'src/store/well-pad-preparation/types';

export type TRSEPreparationValues = {
  landLeaseDate: number | null;
  forestProjectDate: number | null;
  routeReadyDate: number | null;
  routeDocumentationDate: number | null;
  electricityDate: number | null;
  padReadyDate: number | null;
};

const getReadinessStatusByDates = (currentDate: number, date: number | null): TPreparationStatus => {
  if (!hasValue(date)) {
    return 'notReady';
  }

  if (currentDate >= date) {
    return 'ready';
  }

  return 'inProcess';
};

export class RSEReadiness {
  private readonly rawRse: RigsChart.RawPad<TRSEPreparationValues>;

  constructor(rawRse: RigsChart.RawPad) {
    this.rawRse = rawRse as RigsChart.RawPad<TRSEPreparationValues>;
  }

  get RSEPreparationValues(): TRSEPreparationValues {
    const { items } = this.rawRse;

    return items[0];
  }

  get padReadiness(): TPadReadiness {
    const currentUnixTime = millisecondsToSeconds(Date.now());
    const { padReadyDate, landLeaseDate, forestProjectDate, routeReadyDate, routeDocumentationDate, electricityDate } =
      this.RSEPreparationValues;

    const padReadiness: TPreparationStatus = (() => {
      if (!hasValue(padReadyDate)) {
        return 'notReady';
      }

      const currentUnixTime = millisecondsToSeconds(Date.now());

      if (currentUnixTime >= padReadyDate) {
        return 'ready';
      }

      return 'inProcess';
    })();
    const padDocumentationReadiness: TPreparationStatus = (() => {
      const landReady = getReadinessStatusByDates(currentUnixTime, landLeaseDate);
      const forestReady = getReadinessStatusByDates(currentUnixTime, forestProjectDate);

      const _ = [landReady, forestReady] as const;

      if (_.includes('notReady')) {
        return 'notReady';
      } else if (_.includes('inProcess')) {
        return 'inProcess';
      }

      return 'ready';
    })();
    const routeReadiness = getReadinessStatusByDates(currentUnixTime, routeReadyDate);
    const routeDocumentationReadiness = getReadinessStatusByDates(currentUnixTime, routeDocumentationDate);
    const electricityReadiness = getReadinessStatusByDates(currentUnixTime, electricityDate);

    return {
      padReadiness,
      padDocumentationReadiness,
      routeReadiness,
      routeDocumentationReadiness,
      electricityReadiness,
    };
  }
}
